import { hasMeetDependencies, hasMeetExclusions } from '@utils/form'
import { isNullOrUndefined, isObject } from '@utils/types'
import { createElement } from 'react'

/**
 * Recursively renders form fields from a model definition, binding each field's value/onChange to the
 * parent data object and handling nested models, repeater min/max bindings, dependsOn/excludeOn
 * conditionals, and per-field default values.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.model - Field-definition map describing each field to render.
 * @param {Object} props.data - Current form data, keyed by field name.
 * @param {Function} props.setData - Callback invoked with updated data when a field changes.
 * @param {Object} props.errors - Validation errors, keyed by field name.
 * @param {Boolean} props.processing - Whether the form is submitting; disables fields when true.
 * @param {Boolean} [props.repeatable] - Whether this form instance is rendered inside a repeater,
 *   enabling string-based min/max field bindings resolved against sibling data.
 * @returns {JSX.Element}
 */
export default function FormContent(props) {
  /**
   * Extracts the value to store from a field's change event, based on the field component's type.
   *
   * @param {Object} e - Change event (or raw value for CodeMirror) emitted by the field component.
   * @param {string} fieldComponent - Field's component displayName, used to determine how to extract the value.
   * @returns {*} Extracted field value.
   */
  const fieldValue = (e, fieldComponent) => {
    switch (fieldComponent) {
      case 'Checkbox':
        return e.checked
      case 'CodeMirror':
        return e
      case 'InputMask':
      case 'InputText':
      case 'InputTextarea':
      case 'Mention':
      case 'Password':
        return e.target.value
      default:
        return e.value
    }
  }

  /**
   * Updates the form data by setting a single field's value and propagating the merged object via setData.
   *
   * @param {string} key - Field name to update.
   * @param {*} value - New value for the field.
   * @returns {void}
   */
  const updateData = (key, value) => {
    props.setData({ ...props.data, [key]: value })
  }

  /**
   * Builds the attrs/options object passed to a field's component for the given model key, resolving
   * onChange handling, default values, disabled state and error classes based on the field's type.
   *
   * @param {string} key - Field name within props.model to build options for.
   * @returns {Object} Attrs object to spread onto the rendered field component.
   */
  const options = key => {
    let defaultValue = props.model[key].defaultValue ?? null
    let defaultValueSetFromData = props.data[key]
    const attrs = {
      ...props.model[key].options,
      onChange: e => updateData(key, fieldValue(e, props.model[key].field.displayName)),
      className: props.errors[key] ? 'p-invalid' : props.model[key].options?.className || '',
      errors: props.errors[key]
    }

    // Override default options if the form is processing, disable fields
    if (props.processing) {
      attrs.disabled = true
    }

    const fieldName = props.model[key].field.displayName ?? props.model[key].field.name

    switch (fieldName) {
      case 'InputText':
      case 'InputTextarea':
      case 'DateInput':
      case 'DatetimeInput':
      case 'DateDayMonthInput':
        // For input type text default value must be string to prevent uncontrolled input
        // @see https://react.dev/reference/react-dom/components/input#controlling-an-input-with-a-state-variable
        defaultValue = defaultValue ?? ''
        break
      case 'InputNumber':
        attrs.onValueChange = attrs.onChange
        attrs.max = attrs.max ?? Number.MAX_SAFE_INTEGER
        attrs.min = attrs.min ?? Number.MIN_SAFE_INTEGER
        break
      case 'InputSwitch':
      case 'Checkbox':
        defaultValue = defaultValue ?? false
        attrs.checked = defaultValueSetFromData ?? defaultValue
        break
      case 'MultiSelect':
        if (props.model[key].options && props.model[key].options['required']) {
          // Return null if array is empty to trigger required validation
          attrs.onChange = e => {
            const value = e.value && e.value.length > 0 ? e.value : null
            updateData(key, value)
          }
        }
        break
      default:
        break
    }

    // Update data according to defaultValue
    if (isNullOrUndefined(defaultValueSetFromData) && !isNullOrUndefined(defaultValue)) {
      props.data[key] = defaultValue
      defaultValueSetFromData = defaultValue
    }

    // Update field value attribute
    attrs.value = defaultValueSetFromData

    return attrs
  }

  /**
   * Determines whether a given field should be rendered in the form, evaluating the displayInForm/
   * displayInOverview flags and any dependsOn/excludeOn conditionals against the current (and
   * model-default-filled) data.
   *
   * @param {string} key - Field name within props.model to evaluate.
   * @returns {boolean} Whether the field should be displayed.
   */
  const show = key => {
    if (props.model[key].displayInForm === false) {
      return false
    }

    // Prevent displaying overview fields and non-object fields
    if (props.model[key].displayInOverview || !isObject(props.model[key])) {
      return false
    }

    // Build effective data by merging defaultValues from the model
    // This ensures dependsOn checks work even if a field hasn't been rendered yet
    const effectiveData = { ...props.data }
    Object.keys(props.model).forEach(key => {
      if (
        isObject(props.model[key]) &&
        effectiveData[key] === undefined &&
        props.model[key].defaultValue !== undefined
      ) {
        effectiveData[key] = props.model[key].defaultValue
      }
    })

    // Handle dependsOn conditions
    if (props.model[key].dependsOn) {
      return hasMeetDependencies(props.model[key], effectiveData)
    }

    // Handle excludeOn conditions
    if (props.model[key].excludeOn) {
      return hasMeetExclusions(props.model[key], effectiveData)
    }

    return true
  }

  return (
    props.model &&
    Object.keys(props.model)
      .filter(key => show(key))
      .map(key => {
        if (props.model[key].model && props.model[key].model) {
          return createElement(props.model[key].field, {
            key,
            title: props.model[key].label,
            buttonLabel: props.model[key].buttonLabel,
            model: props.model[key].model,
            data: props.data[key] ?? [],
            max: props.model[key].max,
            loading: props.model[key].loading,
            errors: props.errors[key] ? props.errors[key] : {},
            setData: value => updateData(key, value),
            fieldKey: key
          })
        } else if (!props.model[key].field) {
          return (
            <FormContent
              key={key}
              model={props.model[key]}
              data={props.data[key] ? props.data[key] : {}}
              setData={value => updateData(key, value)}
              errors={props.errors[key] ? props.errors[key] : {}}
              processing={props.processing}
            />
          )
        } else {
          const baseOptions = options(key)
          const fieldName = props.model[key].field.displayName ?? props.model[key].field.name
          const fieldOptions = {
            ...baseOptions,
            invalid: !!props.errors[key] || baseOptions.invalid
          }

          props.repeatable &&
            fieldOptions.min &&
            'string' === typeof fieldOptions.min &&
            (fieldOptions.min = props.data[fieldOptions.min])

          props.repeatable &&
            fieldOptions.max &&
            'string' === typeof fieldOptions.max &&
            (fieldOptions.max = props.data[fieldOptions.max])

          return (
            <div
              key={key}
              className={`field-container field-${key}`}
            >
              {props.model[key].introText && (
                <p className="field-intro">
                  <i className="fa-solid fa-circle-info" />
                  {props.model[key].introText}
                </p>
              )}

              <div className={`field ${fieldName && fieldName.toLowerCase()}`}>
                {props.model[key].label && (
                  <label
                    className={`label ${props.model[key].options && props.model[key].options['required'] ? 'label-required' : ''}`}
                  >
                    {props.model[key].label}
                  </label>
                )}
                {props.model[key].field && (
                  <div className="field-input">
                    {createElement(props.model[key].field, fieldOptions)}
                  </div>
                )}
                {props.model[key].helpText && <small>{props.model[key].helpText}</small>}
                {props.errors[key] && <p className="error-form">{props.errors[key]}</p>}
              </div>
            </div>
          )
        }
      })
  )
}
